import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import LoadingState from "@/components/LoadingState";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import { Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCaseUpdates, useCase } from "@/services/queries";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";

const CaseUpdates = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    data: updates,
    isLoading: isLoadingUpdates,
    isError: isErrorUpdates,
    refetch: refetchUpdates,
  } = useCaseUpdates(id ?? undefined, !!id);

  const {
    data: grievance,
    isLoading: isLoadingGrievance,
    isError: isErrorGrievance,
    refetch: refetchGrievance,
  } = useCase(id ?? undefined, !!id);

  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      await Promise.all([
        refetchUpdates(),
        id ? refetchGrievance() : Promise.resolve(),
      ]);
    },
    enabled: !!id,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Show loading state
  if (isLoadingUpdates || (id && isLoadingGrievance)) {
    return (
      <div className="min-h-screen bg-background">
        <Header title="Case Updates History" showBackButton transparent />
        <div className="pt-20">
          <LoadingState />
        </div>
      </div>
    );
  }

  // Show error or not found
  if (isErrorUpdates || isErrorGrievance) {
    toast({
      title: "Failed to load case data",
      description: "Something went wrong. Please try again later.",
      variant: "destructive",
    });
    navigate("/not-found");
    return null;
  }

  const grievanceData = grievance ?? { title: "", location: "" };
  const updatesList = updates ?? [];

  return (
    <div
      ref={pullToRefresh.containerRef}
      className="min-h-screen bg-background overflow-auto"
    >
      <PullToRefreshIndicator
        isVisible={pullToRefresh.shouldShowIndicator}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
      />

      <Header title="Case Updates History" showBackButton transparent />
      <main className="px-4 pt-20 pb-20">
        <div className="mb-6">
          <h1 className="text-xl font-bold mb-1">{grievanceData.title}</h1>
          <p className="text-sm text-muted-foreground">{grievanceData.location}</p>
        </div>

        <div className="space-y-6">
          <div className="glass-card p-4 rounded-xl border">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-base font-medium">Updates History</h2>
            </div>

            {updatesList.length > 0 ? (
              <div className="space-y-4">
                {updatesList.map((update, index) => (
                  <div key={index} className="relative pl-4 pb-4 last:pb-0">
                    <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-border" />

                    <p className="text-sm font-medium mb-1">
                      {formatDate(update.date)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {update.content}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p>No updates available for this case yet.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CaseUpdates;
